import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  Stack, Box, Typography, IconButton, Alert, CircularProgress, MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CaptchaWidget from '../../../components/CaptchaWidget';
import { submitSalesInquiry } from '../../../services/stripeApi';

const ACCENT = '#0969da';
const ACCENT_DARK = '#0550ae';
const BORDER = '#d0d7de';
const SURFACE = '#ffffff';
const INK = '#1f2328';
const INK_SOFT = '#57606a';
const SUCCESS = '#1a7f37';

const TEAM_SIZES = ['1–10', '11–50', '51–200', '201–1000', '1000+'];

/**
 * "Contact sales" popup for the signup page. Collects a prospect's details and
 * posts them to the public sales-inquiry endpoint, which emails the plan's
 * targeted sales inbox. Captcha + name/email are required; everything else is
 * optional.
 *
 * Props: open, onClose, planCode, planName, captcha {provider, siteKey, enabled}, isDark
 */
const ContactSalesModal = ({ open, onClose, planCode, planName, captcha, isDark = false }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const paper = isDark ? '#0d1117' : SURFACE;
  const text = isDark ? '#e6edf3' : INK;
  const sub = isDark ? '#9da7b3' : INK_SOFT;
  const fieldBorder = isDark ? '#30363d' : BORDER;

  const reset = () => {
    setName(''); setEmail(''); setCompany(''); setPhone('');
    setTeamSize(''); setMessage(''); setCaptchaToken(null);
    setError(null); setDone(false); setSubmitting(false);
  };

  const handleClose = () => { if (!submitting) { reset(); onClose?.(); } };

  const handleSubmit = async () => {
    if (!name.trim()) return setError('Please enter your name');
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return setError('Please enter a valid work email');
    if (captcha?.enabled && captcha.provider && captcha.provider !== 'RECAPTCHA_V3' && !captchaToken) {
      return setError('Please complete the captcha');
    }
    setError(null);
    setSubmitting(true);
    try {
      await submitSalesInquiry({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        company: company.trim() || undefined,
        phone: phone.trim() || undefined,
        teamSize: teamSize || undefined,
        planCode: planCode || undefined,
        message: message.trim() || undefined,
        captchaToken: captchaToken || undefined,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: paper, color: text, border: '1px solid', borderColor: fieldBorder } }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
        {done ? 'Message sent' : 'Talk with our sales team'}
        <IconButton onClick={handleClose} disabled={submitting} sx={{ position: 'absolute', right: 12, top: 12, color: sub }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {done ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 3, textAlign: 'center' }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 56, color: SUCCESS }} />
            <Typography variant="h6" fontWeight={700}>Thanks, {name.split(' ')[0] || 'there'}!</Typography>
            <Typography variant="body2" sx={{ color: sub, maxWidth: 380 }}>
              Our sales team has your details{planName ? ` for the ${planName} plan` : ''} and will reach out
              to <strong>{email}</strong> shortly.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: sub }}>
              Tell us a bit about your team{planName ? ` and your interest in ${planName}` : ''}. We'll get back to you
              with a tailored plan.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Full name" required value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
              <TextField label="Work email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth size="small" />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} fullWidth size="small" />
              <TextField label="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth size="small" />
            </Stack>
            <TextField select label="Team size (optional)" value={teamSize} onChange={(e) => setTeamSize(e.target.value)} fullWidth size="small">
              <MenuItem value=""><em>Prefer not to say</em></MenuItem>
              {TEAM_SIZES.map((t) => <MenuItem key={t} value={t}>{t} people</MenuItem>)}
            </TextField>
            <TextField
              label="What are you looking to monitor? (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth multiline minRows={3} size="small"
            />
            {captcha?.enabled && captcha.provider && (
              <CaptchaWidget
                provider={captcha.provider}
                siteKey={captcha.siteKey}
                onVerify={setCaptchaToken}
                onExpire={() => setCaptchaToken(null)}
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {done ? (
          <Button onClick={handleClose} variant="contained" sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: ACCENT_DARK } }}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none', color: sub }}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
              endIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
              sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 700, px: 3, '&:hover': { bgcolor: ACCENT_DARK } }}
            >
              {submitting ? 'Sending…' : 'Send message'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactSalesModal;
